"use client";

import { useMotionValue, useTransform, motion } from "framer-motion";
import React, { useRef, useEffect } from "react";

export const Particles = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext("2d")!;
    let width = (canvas.width = window.innerWidth);
    let height = (canvas.height = window.innerHeight / 1.5);

    const handleResize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight / 1.5;
    };
    window.addEventListener("resize", handleResize);

    const blobs = Array.from({ length: 6 }).map(() => ({
      x: Math.random() * width,
      y: Math.random() * (height * 0.9),
      r: 250 + Math.random() * 250,
      color: `hsla(${Math.random() * 360}, 90%, 65%, 0.6)`,
      dx: (Math.random() - 0.5) * 2, // faster movement
      dy: (Math.random() - 0.5) * 2,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      ctx.globalCompositeOperation = "lighter";
      blobs.forEach((b) => {
        const gradient = ctx.createRadialGradient(b.x, b.y, 0, b.x, b.y, b.r);
        gradient.addColorStop(0, b.color);
        gradient.addColorStop(1, "transparent");
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2);
        ctx.fill();

        b.x += b.dx;
        b.y += b.dy;

        if (b.x < -b.r || b.x > width + b.r) b.dx *= -1;
        if (b.y < -b.r || b.y > height + b.r) b.dy *= -1;
      });
      requestAnimationFrame(draw);
    };

    draw();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute top-0 left-0 w-full"
      style={{
        height: "60vh", // only top part
        filter: "blur(120px) brightness(1.3)",
        zIndex: 0,
      }}
    />
  );
};

export default function EffectVideo() {
  return (
    <div className="relative w-full h-full overflow-hidden rounded-none opacity-[0.02]">
      <video
        src="/hero.webm"
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        className="w-full h-full object-cover bg-[#020202]"
        style={{
          objectPosition: "50% 50%",
        }}
      />
    </div>
  );
}

interface GlowCanvasProps {
  color?: string; // glow color, e.g. "#ff00ff"
  particleCount?: number;
}

export const GlowCanvas = ({
  color = "#00b3ff",
  particleCount = 40,
}: GlowCanvasProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d")!;
    let width = (canvas.width = window.innerWidth);
    let height = (canvas.height = window.innerHeight);

    const handleResize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", handleResize);

    const particles = Array.from({ length: particleCount }, () => ({
      x: Math.random() * width,
      y: Math.random() * height,
      radius: Math.random() * 3 + 1,
      dx: (Math.random() - 0.5) * 0.6,
      dy: (Math.random() - 0.5) * 0.6,
    }));

    function animate() {
      ctx.clearRect(0, 0, width, height);

      particles.forEach((p) => {
        p.x += p.dx;
        p.y += p.dy;

        if (p.x < 0 || p.x > width) p.dx *= -1;
        if (p.y < 0 || p.y > height) p.dy *= -1;

        const gradient = ctx.createRadialGradient(
          p.x,
          p.y,
          0,
          p.x,
          p.y,
          p.radius * 20
        );
        gradient.addColorStop(0, `${color}aa`);
        gradient.addColorStop(1, "transparent");

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius * 20, 0, Math.PI * 2);
        ctx.fill();
      });

      requestAnimationFrame(animate);
    }

    animate();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [color, particleCount]);

  return (
    <div className="w-full h-full">
      <canvas ref={canvasRef} className="w-full h-full block" />
    </div>
  );
};
